import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  BookOpen,
  Package,
  FileText,
  Target,
  Lightbulb,
  Sparkles,
  Plus,
  ArrowRight,
  Search,
  CheckCircle2,
  CircleDashed,
  Clock,
  AtSign,
  Globe,
  FileUp,
  PenLine,
} from "lucide-react";
import { useCompany, useCompanyId } from "@/lib/CompanyContext";
import { knowledgeApi, type KnowledgeBase, type KnowledgeBaseType } from "@/api/knowledge";
import { authApi } from "@/lib/auth";
import { queryKeys } from "@/lib/queryKeys";
import { HeroKnowledge } from "@/components/HeroKnowledge";

type FilterKey = "all" | KnowledgeBaseType;

// Cor POR TIPO: com muitas bases, o laranja único virava um mar de cartões
// idênticos — cor é o que o olho escaneia antes de ler. Produto mantém o
// laranja da marca; os demais ganham matizes calibrados (mesma família de
// acentos do Quadro) pra escanear sem brigar com a identidade.
const TYPE_META: Record<
  KnowledgeBaseType,
  { label: string; plural: string; icon: typeof BookOpen; color: string }
> = {
  onboarding: { label: "Onboarding", plural: "Onboarding", icon: BookOpen, color: "var(--nd-blue)" },
  product: { label: "Produto", plural: "Produtos", icon: Package, color: "var(--nd-orange)" },
  material: { label: "Material", plural: "Materiais", icon: FileText, color: "#0e7490" },
  competitor: { label: "Concorrente", plural: "Concorrentes", icon: Target, color: "#d92d4a" },
  reference: { label: "Referência", plural: "Referências", icon: Lightbulb, color: "#8b5cf6" },
  custom: { label: "Custom", plural: "Custom", icon: Sparkles, color: "#3aa657" },
};

/** Ordem das seções na aba "Todas" (agrupamento por tipo). */
const TYPE_ORDER: KnowledgeBaseType[] = [
  "product",
  "material",
  "competitor",
  "reference",
  "custom",
];

/** Origem da base (de onde o conteúdo veio) — selo discreto no rodapé do card. */
function getOrigin(kb: KnowledgeBase): { icon: typeof Globe; label: string } | null {
  if (kb.type === "onboarding") return null;
  const src = (kb.contentMeta as { source?: { originalUrl?: string } } | null)?.source;
  if (/instagram\.com/i.test(src?.originalUrl ?? "")) return { icon: AtSign, label: "Instagram" };
  if (kb.sourceKind === "pdf") return { icon: FileUp, label: "PDF" };
  if (kb.sourceKind === "csv") return { icon: FileUp, label: "CSV" };
  if (kb.sourceKind === "url") return { icon: Globe, label: "site" };
  if (kb.sourceKind === "manual") return { icon: PenLine, label: "manual" };
  return null;
}

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "Todas" },
  { key: "onboarding", label: "Onboarding" },
  { key: "product", label: "Produtos" },
  { key: "material", label: "Materiais" },
  { key: "competitor", label: "Concorrentes" },
  { key: "reference", label: "Referências" },
];

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "short",
  }).format(new Date(iso));
}

function getCharCount(kb: KnowledgeBase): number {
  return kb.contentText.length;
}

function getStatusBadge(kb: KnowledgeBase) {
  if (kb.status === "ready") {
    if (kb.contentText.trim().length === 0 && kb.type === "onboarding") {
      return { icon: CircleDashed, label: "vazio", color: "var(--wf-ink-3)" };
    }
    return { icon: CheckCircle2, label: "ativa", color: "#5cb85c" };
  }
  if (kb.status === "processing") {
    return { icon: Clock, label: "processando", color: "var(--nd-orange)" };
  }
  return { icon: CircleDashed, label: "falha", color: "#ff5577" };
}

function KnowledgeCard({ kb, onClick }: { kb: KnowledgeBase; onClick: () => void }) {
  const meta = TYPE_META[kb.type];
  const Icon = meta.icon;
  const status = getStatusBadge(kb);
  const StatusIcon = status.icon;
  const chars = getCharCount(kb);
  const isOnboarding = kb.type === "onboarding";

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      className="wf-card"
      style={{
        textAlign: "left",
        cursor: "pointer",
        padding: 18,
        background: "var(--wf-paper)",
        border: isOnboarding
          ? "1px solid var(--nd-orange)"
          : "1px solid var(--wf-ink-5)",
        // Acento lateral na COR DO TIPO — identifica a base de relance,
        // antes de ler o título (mesma linguagem do Kanban por status).
        borderLeft: isOnboarding ? "3px solid var(--nd-orange)" : `3px solid ${meta.color}`,
        boxShadow: isOnboarding
          ? "0 0 0 3px rgba(255,112,0,0.08)"
          : "0 1px 0 rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minHeight: 168,
        position: "relative",
      }}
    >
      {/* Header: icon + type label + status */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: `${meta.color}18`,
            color: meta.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={15} strokeWidth={1.75} />
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--wf-ink-2)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {meta.label}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
          <StatusIcon size={11} strokeWidth={2} color={status.color} />
          <span style={{ fontSize: 10, color: status.color, fontWeight: 500 }}>{status.label}</span>
        </div>
      </div>

      {/* Title */}
      <div
        className="wf-display"
        style={{
          fontSize: 17,
          fontWeight: 700,
          letterSpacing: "-0.015em",
          lineHeight: 1.15,
          color: "var(--wf-ink)",
        }}
      >
        {kb.name}
      </div>

      {/* Description */}
      {kb.description && (
        <div
          style={{
            fontSize: 12,
            color: "var(--wf-ink-2)",
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {kb.description}
        </div>
      )}

      {/* Footer: meta (origem · tamanho · atualização) */}
      <div
        style={{
          marginTop: "auto",
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 11,
          color: "var(--wf-ink-3)",
        }}
      >
        {(() => {
          const origin = getOrigin(kb);
          if (!origin) return null;
          const OriginIcon = origin.icon;
          return (
            <>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <OriginIcon size={11} strokeWidth={1.75} />
                {origin.label}
              </span>
              <span>·</span>
            </>
          );
        })()}
        <span>{chars.toLocaleString("pt-BR")} caracteres</span>
        <span>·</span>
        <span>atualizada {formatDate(kb.updatedAt)}</span>
      </div>
    </motion.button>
  );
}

function EmptyOnboardingCard() {
  return (
    <Link
      to="/briefing/onboarding"
      className="wf-card"
      style={{
        textDecoration: "none",
        padding: 22,
        background: "linear-gradient(135deg, #fff8ef 0%, #ffffff 100%)",
        border: "1px solid var(--nd-orange)",
        boxShadow: "0 0 0 3px rgba(255,112,0,0.10)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 168,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: "var(--nd-orange)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BookOpen size={18} strokeWidth={1.75} />
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--nd-orange-pun)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Onboarding
          </div>
          <div
            className="wf-display"
            style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.015em", color: "var(--wf-ink)" }}
          >
            Comece pela base
          </div>
        </div>
      </div>
      <div style={{ fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.5 }}>
        Responde 5 perguntas sobre você e a empresa. Esse contexto fica sempre ativo nos
        agentes, então eles sabem quem você é em qualquer tarefa.
      </div>
      <div
        style={{
          marginTop: "auto",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          color: "var(--nd-orange-pun)",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        Iniciar onboarding <ArrowRight size={13} strokeWidth={2} />
      </div>
    </Link>
  );
}

function NewKbCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="wf-card"
      style={{
        cursor: "pointer",
        padding: 22,
        background: "transparent",
        border: "1.5px dashed var(--wf-ink-4)",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        minHeight: 168,
        color: "var(--wf-ink-2)",
        transition: "border-color 150ms ease, color 150ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--nd-orange)";
        e.currentTarget.style.color = "var(--nd-orange-pun)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--wf-ink-4)";
        e.currentTarget.style.color = "var(--wf-ink-2)";
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: "rgba(255,112,0,0.08)",
          color: "var(--nd-orange)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Plus size={20} strokeWidth={2} />
      </div>
      <div style={{ fontWeight: 600, fontSize: 14 }}>Nova base</div>
      <div style={{ fontSize: 12, textAlign: "center", maxWidth: 220 }}>
        Adicione contexto: produto, material, caso, concorrente, persona…
      </div>
    </button>
  );
}

export default function Briefing() {
  const { company, isLoading: companyLoading } = useCompany();
  const companyId = useCompanyId();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [search, setSearch] = useState("");

  const session = useQuery({
    queryKey: ["auth", "session"],
    queryFn: () => authApi.getSession(),
  });

  const list = useQuery({
    queryKey: companyId ? queryKeys.companies.knowledge(companyId) : ["knowledge", "none"],
    queryFn: () => knowledgeApi.list(companyId!),
    enabled: !!companyId,
  });


  const firstName = useMemo(
    () => session.data?.user.name?.split(" ")[0] ?? "por aí",
    [session.data?.user.name],
  );

  const allKbs = list.data ?? [];
  const onboardingKb = allKbs.find((k) => k.type === "onboarding") ?? null;
  const modularKbs = allKbs.filter((k) => k.type !== "onboarding");

  const filteredModular = useMemo(() => {
    // Aba "onboarding" mostra SÓ o card de onboarding (renderizado à parte) —
    // nenhum KB modular. Antes vazava todos os modulares aqui.
    if (filter === "onboarding") return [];
    let result = modularKbs;
    if (filter !== "all") {
      result = result.filter((k) => k.type === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      // Busca cobre nome, descrição, TIPO/produto (label da aba), ORIGEM
      // (sourceKind: pdf/instagram/manual…) e as palavras-chave do KB — assim dá
      // pra filtrar por origem ou produto direto na busca, e o nome de um produto
      // isola só ele.
      result = result.filter((k) => {
        const haystack = [
          k.name,
          k.description ?? "",
          TYPE_META[k.type]?.label ?? k.type,
          k.sourceKind,
          ...(k.triggerKeywords ?? []),
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return result;
  }, [modularKbs, filter, search]);

  if (companyLoading) {
    return (
      <div className="wf" style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando empresa…</span>
      </div>
    );
  }

  if (!company) {
    return (
      <div style={{ padding: 32 }}>
        <div className="wf-card" style={{ padding: 18 }}>
          Carregando seu workspace…
        </div>
      </div>
    );
  }

  const handleNewKb = () => {
    // Quando user clica em "+ Adicionar" estando numa aba específica (produto/
    // concorrente/material), passa o tipo via query param pro NovaKb pular o
    // step "escolher tipo" e ir direto pro form daquele tipo.
    const typeParam =
      filter !== "all" && filter !== "onboarding" ? `?type=${filter}` : "";
    navigate(`/briefing/nova${typeParam}`);
  };

  return (
    <div>
      {/* Hero */}
      <div className="wf-hero" style={{ minHeight: 220 }}>
        <HeroKnowledge />
        <div className="wf-hero__content">
          <motion.h1
            className="wf-hero__greeting"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontSize: 30 }}
          >
            Base de Conhecimento
          </motion.h1>
          <motion.p
            className="wf-hero__sub"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          >
            Tudo que seus agentes sabem sobre {firstName === "por aí" ? "você" : firstName} e a empresa
          </motion.p>
        </div>
      </div>

      {/* Tabs/filters */}
      <div className="wf-tabs" style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`wf-tab ${filter === f.key ? "wf-tab--active" : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            <span style={{ marginLeft: 5, fontSize: 11, color: "var(--wf-ink-3)" }}>
              {f.key === "all"
                ? modularKbs.length + (onboardingKb ? 1 : 0)
                : f.key === "onboarding"
                  ? onboardingKb
                    ? 1
                    : 0
                  : modularKbs.filter((k) => k.type === f.key).length}
            </span>
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ padding: "24px 32px 48px", maxWidth: 1280, margin: "0 auto" }}>
        {/* Search */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 18,
            padding: "8px 12px",
            border: "1px solid var(--wf-ink-5)",
            borderRadius: 8,
            background: "var(--wf-paper)",
            maxWidth: 360,
          }}
        >
          <Search size={14} strokeWidth={1.75} color="var(--wf-ink-3)" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar bases..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 13,
              fontFamily: "var(--wf-font-body)",
            }}
          />
        </div>

        {list.isLoading && (
          <div className="wf-hand-note" style={{ fontSize: 13 }}>
            carregando bases…
          </div>
        )}

        {!list.isLoading && filter === "all" && (
          <>
            {/* Linha fixa do topo: onboarding (a base-mãe) + criar nova */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 14,
              }}
            >
              {onboardingKb ? (
                <KnowledgeCard
                  kb={onboardingKb}
                  onClick={() => navigate(`/briefing/onboarding`)}
                />
              ) : (
                <EmptyOnboardingCard />
              )}
              <NewKbCard onClick={handleNewKb} />
            </div>

            {/* Seções AGRUPADAS por tipo — com muitas bases, achar "aquele
                concorrente" no grid misturado exigia ler card por card. */}
            {TYPE_ORDER.map((type) => {
              const items = filteredModular.filter((k) => k.type === type);
              if (items.length === 0) return null;
              const meta = TYPE_META[type];
              return (
                <div key={type}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      margin: "24px 0 10px",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: meta.color,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "var(--wf-ink-2)",
                      }}
                    >
                      {items.length > 1 ? meta.plural : meta.label} · {items.length}
                    </span>
                    <span style={{ flex: 1, height: 1, background: "var(--wf-ink-5)" }} />
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                      gap: 14,
                    }}
                  >
                    {items.map((kb) => (
                      <KnowledgeCard
                        key={kb.id}
                        kb={kb}
                        onClick={() => navigate(`/briefing/${kb.id}`)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {!list.isLoading && filter !== "all" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 14,
            }}
          >
            {/* Aba Onboarding mostra só a base-mãe (ou o CTA de criar) */}
            {filter === "onboarding" &&
              (onboardingKb ? (
                <KnowledgeCard
                  kb={onboardingKb}
                  onClick={() => navigate(`/briefing/onboarding`)}
                />
              ) : (
                <EmptyOnboardingCard />
              ))}

            {/* KBs modulares do tipo filtrado */}
            {filteredModular.map((kb) => (
              <KnowledgeCard
                key={kb.id}
                kb={kb}
                onClick={() => navigate(`/briefing/${kb.id}`)}
              />
            ))}

            {/* Card de criar nova (apenas filtros que aceitam modulares) */}
            {filter !== "onboarding" && <NewKbCard onClick={handleNewKb} />}
          </div>
        )}

        {/* Empty state filtrado */}
        {!list.isLoading &&
          filter !== "all" &&
          filter !== "onboarding" &&
          filteredModular.length === 0 && (
            <div
              style={{
                marginTop: 24,
                padding: 24,
                textAlign: "center",
                color: "var(--wf-ink-2)",
                fontSize: 13,
                background: "var(--wf-paper)",
                border: "1px dashed var(--wf-ink-4)",
                borderRadius: 10,
              }}
            >
              Nenhuma base do tipo <strong>{TYPE_META[filter as KnowledgeBaseType].label}</strong>{" "}
              ainda. Que tal criar a primeira?
            </div>
          )}

      </div>
    </div>
  );
}
